import { AppProperties, Collection, CollectionItem, Video } from "@/api";
import { getDateFromUTCString, secondsToTime } from "./date.ts";
import {
  CONTENT_TEMPLATES,
  IN_PROGRESS_TEMPLATE,
  ITEM_TYPE_COLLECTION,
  ITEM_TYPE_VIDEO,
  VIDEO_TYPE_POST,
  VIDEO_TYPE_REST,
  VIDEO_UNLOCKED,
  WORKOUT_TEMPLATES,
} from "@/const.ts";
import { checkVideoFile, isLiveVideo } from "./videos.ts";

export const getContentType = (items: Pick<CollectionItem, "Type">[]) => {
  const types = items.reduce((acc, curr) => {
    acc.add(curr.Type);
    return acc;
  }, new Set<string>());

  if (types.size > 1) {
    return "Item"; // Mixed items
  } else if (types.has(ITEM_TYPE_COLLECTION)) {
    return "Collection";
  } else if (types.has(ITEM_TYPE_VIDEO)) {
    return "Video";
  }
  return "Item"; // Fallback
};

const hideItemCounts = (properties: AppProperties) => {
  return properties["HideItemCountOnCollectionThumbnails"] === "1";
};
const hideTimestamps = (properties: AppProperties) => {
  return properties["HideTimestampOnVideoThumbnails"] === "1";
};

export const getContentOverlay = (
  props:
    | Partial<Collection>
    | Partial<Video>
    | Pick<CollectionItem, "ItemCount"> // SearchResult - Collection
    | undefined, // Handle unexpected cases
  properties: AppProperties,
  translate: (key: string) => string,
  formatDetailedDatetime: (date: Date) => string,
) => {
  if (!props) {
    return ""; // Unexpected error: the item was missing when we looked it up
  }

  // Input expects one or the other, but we treat it as both so typescript is happy
  const ambiguousProps = props as Video &
    Collection &
    Pick<CollectionItem, "ItemCount">;
  const isAppOnly = itemRequiresApp(ambiguousProps as Video | Collection);

  if (isAppOnly) {
    return translate("App Only");
  }

  if (ambiguousProps?.Items) {
    // This is a collection where we have the full collection payload
    const { Items } = ambiguousProps;
    const plural = Items.length > 1 ? "s" : "";
    if (hideItemCounts(properties)) {
      return "";
    }
    const itemTypeLabel = `${getContentType(Items).toLowerCase()}${plural}`;
    return Items.length > 0
      ? `${Items.length} ${translate(itemTypeLabel)}`
      : "";
  }
  if (
    ambiguousProps?.Properties?.LiveEventStartDate &&
    isLiveVideo(ambiguousProps)
  ) {
    const liveStartDate = getDateFromUTCString(
      ambiguousProps?.Properties?.LiveEventStartDate,
    );
    if (liveStartDate) {
      const started = !!liveStartDate && liveStartDate < new Date();
      return started ? (
        <div className="flex items-center gap-[2px]">
          <div className="h-1 w-1 rounded-full bg-white" />
          {translate("LIVE")}
        </div>
      ) : (
        `${translate("LIVE")}: ${formatDetailedDatetime(liveStartDate).replace(",", "")}`
      );
    }
  }
  if (ambiguousProps?.DurationSeconds) {
    // This is a video where we either have the CollectionItem/Full Video Payload
    const { DurationSeconds } = ambiguousProps;
    if (hideTimestamps(properties)) {
      return "";
    }
    return secondsToTime(parseInt(DurationSeconds), { hideHours: true });
  } else if (ambiguousProps?.ItemCount) {
    // This is a collection where we just have the CollectionItem
    const { ItemCount } = ambiguousProps;
    const plural = ItemCount > 1 ? "s" : "";
    if (hideItemCounts(properties)) {
      return "";
    }
    return ItemCount > 0 ? `${ItemCount} Item${plural}` : "";
  }
  if (ambiguousProps.VideoId && ambiguousProps.Type === VIDEO_TYPE_POST) {
    return "";
  }
  if (ambiguousProps.OriginalFilename) {
    const type = checkVideoFile(ambiguousProps);
    if (type) {
      return type;
    }
  }

  return "";
};

export const filterUnsupportedItems = (items: CollectionItem[]) => {
  return items.filter(() => {
    return true;
  });
};

export const isItemUnlocked = (item: CollectionItem) => {
  return item.ProductId === VIDEO_UNLOCKED;
};

export const isContentTemplate = (templateId: string) => {
  return CONTENT_TEMPLATES.includes(templateId) || templateId === "30";
};

export const isHeroCarousel = (templateId: string) => {
  return templateId === "52";
};

export const isInProgressTemplate = (templateId: string) => {
  return templateId === IN_PROGRESS_TEMPLATE;
};

export const getContentTitle = (content: Video | Collection) => {
  if ("Name" in content) {
    return content.Name;
  }
  if ("Title" in content) {
    return content.Title;
  }
  return "";
};

const isWorkoutItem = (fullItem: Collection | Video) => {
  if (fullItem.Type === VIDEO_TYPE_REST) {
    return true;
  }

  if (fullItem.Type === ITEM_TYPE_COLLECTION) {
    return WORKOUT_TEMPLATES.includes((fullItem as Collection).TemplateId);
  }

  return false;
};

export const itemRequiresApp = (fullItem?: Collection | Video) => {
  if (!fullItem) {
    return false;
  }

  return isWorkoutItem(fullItem);
};
